import React, { useCallback, useRef, useState } from "react";
import { AnnotationPoint, AnnotationStroke, VideoAnnotation } from "../QuickReview/types";

interface AnnotationOverlayProps {
    annotation?: VideoAnnotation | null;
    drawing: boolean;
    color: string;
    width: number;
    onChange?: (annotation: VideoAnnotation) => void;
}

const VIEWBOX_SIZE = 1000;

const pointsToPath = (points: AnnotationPoint[]): string =>
    points
        .map(
            (point, index) =>
                `${index === 0 ? "M" : "L"} ${point.x * VIEWBOX_SIZE} ${point.y * VIEWBOX_SIZE}`
        )
        .join(" ");

const AnnotationOverlay: React.FC<AnnotationOverlayProps> = ({
    annotation,
    drawing,
    color,
    width,
    onChange,
}) => {
    const svgRef = useRef<SVGSVGElement>(null);
    const activeStrokeRef = useRef<AnnotationStroke | null>(null);
    const [activeStroke, setActiveStroke] = useState<AnnotationStroke | null>(null);

    const getPoint = useCallback((event: React.PointerEvent<SVGSVGElement>): AnnotationPoint => {
        const rect = svgRef.current!.getBoundingClientRect();
        return {
            x: Math.min(Math.max((event.clientX - rect.left) / rect.width, 0), 1),
            y: Math.min(Math.max((event.clientY - rect.top) / rect.height, 0), 1),
        };
    }, []);

    const handlePointerDown = (event: React.PointerEvent<SVGSVGElement>) => {
        if (!drawing || event.button !== 0) return;
        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);

        const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        const stroke: AnnotationStroke = {
            id,
            color,
            width,
            points: [getPoint(event)],
        };
        activeStrokeRef.current = stroke;
        setActiveStroke(stroke);
    };

    const handlePointerMove = (event: React.PointerEvent<SVGSVGElement>) => {
        const currentStroke = activeStrokeRef.current;
        if (!drawing || !currentStroke) return;
        event.preventDefault();
        const nextPoint = getPoint(event);
        const previousPoint = currentStroke.points[currentStroke.points.length - 1];
        const minimumDistance = 0.0015;
        if (
            Math.abs(nextPoint.x - previousPoint.x) < minimumDistance &&
            Math.abs(nextPoint.y - previousPoint.y) < minimumDistance
        ) {
            return;
        }
        const nextStroke = {
            ...currentStroke,
            points: [...currentStroke.points, nextPoint],
        };
        activeStrokeRef.current = nextStroke;
        setActiveStroke(nextStroke);
    };

    const finishStroke = (event: React.PointerEvent<SVGSVGElement>) => {
        const currentStroke = activeStrokeRef.current;
        if (!currentStroke) return;
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }

        const completedStroke =
            currentStroke.points.length === 1
                ? {
                      ...currentStroke,
                      points: [
                          currentStroke.points[0],
                          {
                              x:
                                  currentStroke.points[0].x >= 0.9999
                                      ? currentStroke.points[0].x - 0.0001
                                      : currentStroke.points[0].x + 0.0001,
                              y: currentStroke.points[0].y,
                          },
                      ],
                  }
                : currentStroke;

        onChange?.({
            version: 1,
            strokes: [...(annotation?.strokes || []), completedStroke],
        });
        activeStrokeRef.current = null;
        setActiveStroke(null);
    };

    const strokes = activeStroke
        ? [...(annotation?.strokes || []), activeStroke]
        : annotation?.strokes || [];

    return (
        <svg
            ref={svgRef}
            viewBox={`0 0 ${VIEWBOX_SIZE} ${VIEWBOX_SIZE}`}
            preserveAspectRatio="none"
            className={`absolute inset-0 h-full w-full z-20 ${
                drawing ? "cursor-crosshair touch-none pointer-events-auto" : "pointer-events-none"
            }`}
            aria-label={drawing ? "Draw an annotation on the paused video" : undefined}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={finishStroke}
            onPointerCancel={finishStroke}
        >
            {strokes.map(stroke => (
                <path
                    key={stroke.id}
                    d={pointsToPath(stroke.points)}
                    fill="none"
                    stroke={stroke.color}
                    strokeWidth={stroke.width}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    vectorEffect="non-scaling-stroke"
                />
            ))}
        </svg>
    );
};

export default AnnotationOverlay;
